import React from 'react';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetPurchaserDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import { teamName } from '../../../utils/teamName';

interface PurchaserSearchProps {
  hasClient?: boolean;
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh: boolean;
}

export const purchaserForGrid = (
  t: TFunction<'clientLng', 'clientLng'>,
  tk: TKeys<'clientLng'>,
  purchaser: GetPurchaserDto,
  orderBy?: string,
) => {
  let teams = purchaser.teams?.map(team => ({
    name: teamName(team, team.company.shortname),
    systemNumber: team.systemNumber,
  }));
  if (teams && orderBy !== 'teams.name') {
    teams = [...teams].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }
  return {
    key: purchaser.id,
    values: {
      'purchaser.systemNumber': purchaser.systemNumber,
      'purchaser.name': purchaser.name,
      'company.shortname': purchaser.company.shortname,
      'company.systemNumber': purchaser.company.systemNumber,
      'client.name': purchaser.client.name,
      'client.systemNumber': purchaser.client.systemNumber,
      'budget.name': purchaser.budget.name,
      'budget.systemNumber': purchaser.budget.systemNumber,
      'manager.name': purchaser.budget.manager.name,
      'manager.oneToolId': purchaser.budget.manager.oneToolId,
      'teams.name': teams?.map(team => team.name).join(', '),
      'teams.systemNumber': teams?.map(team => team.systemNumber).join(', '),
      purchaser_active_yes_no: purchaser.active
        ? t(tk.common.yesUpperCase)
        : t(tk.common.noUpperCase),
    },
    isInactive: !purchaser.active,
  };
};

export const PurchaserSearch: React.FC<PurchaserSearchProps> = props => {
  const { t, tk } = useTranslation('clientLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'purchaser.systemNumber' },
    { header: t(tk.common.name), property: 'purchaser.name' },
    ...(props.hasClient
      ? []
      : [
          { header: t(tk.common.contractor), property: 'client.name' },
          {
            header: `${t(tk.common.contractor)} - ${t(tk.common.sysNumber)}`,
            property: 'client.systemNumber',
            exportOnly: true,
          },
        ]),
    { header: t(tk.common.budget), property: 'budget.name' },
    {
      header: `${t(tk.common.budget)} - ${t(tk.common.sysNumber)}`,
      property: 'budget.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.purchaser.manager), property: 'manager.name' },
    {
      header: `${t(tk.purchaser.manager)} - ${t(tk.common.oneToolId)}`,
      property: 'manager.oneToolId',
      exportOnly: true,
    },
    { header: t(tk.common.company), property: 'company.shortname' },
    {
      header: `${t(tk.common.company)} - ${t(tk.common.sysNumber)}`,
      property: 'company.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.common.teams), property: 'teams.name' },
    {
      header: `${t(tk.common.teams)} - ${t(tk.common.sysNumber)}`,
      property: 'teams.systemNumber',
      exportOnly: true,
    },
    { header: t(tk.common.activeHe), property: 'purchaser_active_yes_no' },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="purchaser.name"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={
        props.hasClient
          ? t(tk.purchaser.noPurchasersForClient)
          : t(tk.purchaser.noPurchasers)
      }
      onRowClick={props.onRowClick}
      withHistory={true}
      refresh={props.refresh}
      withFilterInColumns={true}
      showExport={true}
    />
  );
};
